import React, { useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const courseOptions = [
  "11th Science",
  "12th Science",
  "NEET Preparation",
  "JEE Main & Advanced",
  "MHT-CET Preparation",
  "Other",
];

export default function EnquiryForm({ variant = "default" }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    course: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error'
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!form.full_name.trim()) newErrors.full_name = "Name is required";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email))
      newErrors.email = "Valid email is required";
    if (!form.phone.trim() || form.phone.length < 10)
      newErrors.phone = "Valid phone number is required";
    if (!form.course) newErrors.course = "Please select a course";
    return newErrors;
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setLoading(true);
    setStatus(null);
    try {
      await axios.post(`${BACKEND_URL}/api/enquiry`, form);
      setStatus("success");
      setForm({ full_name: "", email: "", phone: "", course: "", message: "" });
    } catch (err) {
      setStatus("error");
    }
    setLoading(false);
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 rounded-lg border text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-900/20 ${
      errors[field] ? "border-red-400 bg-red-50" : "border-gray-200 bg-gray-50 focus:bg-white focus:border-[#0A2540]"
    }`;

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="enquiry-form"
      className={variant === "dark" ? "space-y-4" : "space-y-4"}
    >
      {status === "success" && (
        <div
          data-testid="form-success-msg"
          className="bg-green-50 border border-green-200 text-green-800 text-sm px-4 py-3 rounded-lg flex items-center gap-2"
        >
          <span className="text-green-500 text-lg">&#10003;</span>
          Thank you! Your enquiry has been submitted. We'll contact you shortly.
        </div>
      )}
      {status === "error" && (
        <div
          data-testid="form-error-msg"
          className="bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-3 rounded-lg"
        >
          Something went wrong. Please try again or call us directly.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
            Full Name *
          </label>
          <input
            type="text"
            name="full_name"
            value={form.full_name}
            onChange={handleChange}
            placeholder="Enter your full name"
            className={inputClass("full_name")}
            data-testid="form-name-input"
          />
          {errors.full_name && (
            <p className="text-xs text-red-500 mt-1">{errors.full_name}</p>
          )}
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
            Phone Number *
          </label>
          <input
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder="Your mobile number"
            className={inputClass("phone")}
            data-testid="form-phone-input"
          />
          {errors.phone && (
            <p className="text-xs text-red-500 mt-1">{errors.phone}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
          Email Address *
        </label>
        <input
          type="email"
          name="email"
          value={form.email}
          onChange={handleChange}
          placeholder="your@email.com"
          className={inputClass("email")}
          data-testid="form-email-input"
        />
        {errors.email && (
          <p className="text-xs text-red-500 mt-1">{errors.email}</p>
        )}
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
          Course Interested In *
        </label>
        <select
          name="course"
          value={form.course}
          onChange={handleChange}
          className={inputClass("course")}
          data-testid="form-course-select"
        >
          <option value="">Select a course...</option>
          {courseOptions.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        {errors.course && (
          <p className="text-xs text-red-500 mt-1">{errors.course}</p>
        )}
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
          Message (Optional)
        </label>
        <textarea
          name="message"
          value={form.message}
          onChange={handleChange}
          placeholder="Tell us about your needs, current class, target exam..."
          rows={4}
          className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-900/20 focus:bg-white focus:border-[#0A2540] resize-none"
          data-testid="form-message-input"
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="form-submit-btn"
        className="w-full text-white py-3.5 rounded-lg font-bold text-sm tracking-wide transition-all duration-200 hover:opacity-90 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed"
        style={{ backgroundColor: "#0A2540" }}
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            Submitting...
          </span>
        ) : (
          "Submit Enquiry"
        )}
      </button>
    </form>
  );
}
